import { useState, useEffect, useRef } from "react"

import { Typography, Flex } from "antd"

import { getBulkOrderById } from "@/http/bulkOrder"

import { BulkOrderStatus } from "@/types/IBulkOrder"
import { TimerType } from "@/types/ITimer"

import { addUtcTimezoneMarkToDateString } from "@/utils/isBulkOrderTimerExpired"

const { Text } = Typography

export interface IBulkOrderTimerProps {
  id: number
  type: TimerType
  status: BulkOrderStatus
  startUtc: string
  duration: number
  onExpire?: () => void
  textColor?: string
}

export const BulkOrderTimer = (props: IBulkOrderTimerProps) => {
  const { id, type, status, startUtc, duration, textColor, onExpire } = props

  const [timerText, setTimerText] = useState<string>("")
  
  const [overrideParams, setOverrideParams] = useState<{ startUtc: string; duration: number } | null>(null)

  const currentStartUtc = overrideParams ? overrideParams.startUtc : startUtc
  const currentDuration = overrideParams ? overrideParams.duration : duration

  useEffect(() => {
    setOverrideParams(null)
  }, [id])

  useEffect(() => {
    const startTimerUtcZ = addUtcTimezoneMarkToDateString(currentStartUtc)
    const startTimestamp = new Date(startTimerUtcZ).getTime()

    const getRemainingTime = () => {
      const nowTimestamp = new Date().getTime()
      const diffInSeconds = Math.floor(Math.abs(nowTimestamp - startTimestamp) / 1000)
      return currentDuration - diffInSeconds
    }

    let remainingTime = getRemainingTime()

    if (remainingTime <= 0) {
      setTimerText("")
      handleExpire()
      return
    }

    const updateTimerText = (secondsLeft: number) => {
      const hours = Math.floor(secondsLeft / 3600)
      const minutes = Math.floor((secondsLeft % 3600) / 60)
      const seconds = secondsLeft % 60
      setTimerText(`${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`)
    }

    updateTimerText(remainingTime)
    const intervalId = setInterval(() => {
      remainingTime--

      if (remainingTime <= 0) {
        clearInterval(intervalId)
        setTimerText("")
        handleExpire()
      } else {
        updateTimerText(remainingTime)
      }
    }, 1000)

    async function handleExpire() {
      if (onExpire) onExpire()
      if (!id) return

      try {
        const bulkOrder = await getBulkOrderById(id)
        if (bulkOrder?.response) {
          if (type === TimerType.BulkOrderAdmin && bulkOrder.response.timerAdminStart && bulkOrder.response.timerAdminDuration) {
            setOverrideParams({
              startUtc: bulkOrder.response.timerAdminStart,
              duration: bulkOrder.response.timerAdminDuration
            })
          } else if (type === TimerType.BulkOrderCustomer && bulkOrder.response.timerCustomerStart && bulkOrder.response.timerCustomerDuration) {
            setOverrideParams({
              startUtc: bulkOrder.response.timerCustomerStart,
              duration: bulkOrder.response.timerCustomerDuration
            })
          }
        }
      } catch {}
    }

    return () => clearInterval(intervalId)

  }, [id, type, currentStartUtc, currentDuration, onExpire])

  const getDescription = () => {
    if (status === BulkOrderStatus.InProcess) return "Будет рассмотрено"
    if (status === BulkOrderStatus.CurrentOffer) return "Предложение действует"
    return ""
  }

  return (
    <Flex vertical={true} align="center" style={{ width: "max-content" }}>
      {timerText !== "" && (
        <>
          <Text style={{ color: textColor ?? "" }}>{getDescription()}</Text>
          <Text style={{ color: textColor ?? "" }}>{timerText}</Text>
        </>
      )}
    </Flex>
  )
}

export default BulkOrderTimer